import { useState } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  FolderOpen,
  Settings,
  ChevronLeft,
  ChevronRight,
  Building2,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext.tsx';

// ─── Módulos del sidebar (paths relativos al slug) ────────────────────────────
const NAV_MODULES = [
  { label: 'Dashboard',     segment: 'dashboard',            icon: LayoutDashboard, roles: null },
  { label: 'RRHH',          segment: 'rrhh',                 icon: Users,           roles: null },
  { label: 'Documentos',    segment: 'documentos',           icon: FolderOpen,      roles: null },
  { label: 'Facturación',   segment: 'facturacion',          icon: FileText,        roles: null },
  { label: 'Usuarios',      segment: 'usuarios',             icon: ShieldCheck,     roles: ['admin', 'superadmin'] },
  { label: 'Configuración', segment: 'configuracion-fiscal', icon: Settings,        roles: null },
] as const;

interface SidebarProps {
  tenantName: string;
}

export function Sidebar({ tenantName }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { user } = useAuth();
  const userRole = user?.role ?? 'user';

  const visibleModules = NAV_MODULES.filter(m =>
    m.roles === null || (m.roles as readonly string[]).includes(userRole)
  );

  return (
    <aside
      style={{
        width: collapsed ? 64 : 220,
        minHeight: '100vh',
        background: 'var(--color-sidebar)',
        borderRight: '1px solid var(--color-border)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        flexShrink: 0,
        position: 'relative',
        zIndex: 10,
      }}
    >
      {/* Logotipo / nombre de empresa */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          padding: collapsed ? '1.25rem 0' : '1.25rem 1rem',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid var(--color-border)',
          minHeight: 60,
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            background: 'var(--color-accent)',
            color: '#fff',
            borderRadius: 8,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Building2 size={18} />
        </span>
        {!collapsed && (
          <span
            style={{
              fontWeight: 700,
              fontSize: '0.875rem',
              color: 'var(--color-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {tenantName}
          </span>
        )}
      </div>

      {/* Navegación */}
      <nav
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: '0.75rem 0.5rem',
        }}
      >
        {visibleModules.map(({ label, segment, icon: Icon }) => {
          const to = `/${tenantSlug ?? ''}/${segment}`;
          return (
            <NavLink
              key={segment}
              to={to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '0.625rem',
                padding: collapsed ? '0.625rem 0' : '0.625rem 0.75rem',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: '0.8125rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? 'var(--color-accent)' : 'var(--color-muted)',
                background: isActive ? 'var(--color-sidebar-active)' : 'transparent',
                transition: 'background 0.15s, color 0.15s',
              })}
              title={collapsed ? label : undefined}
            >
              <Icon size={16} />
              {!collapsed && <span>{label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Botón colapsar */}
      <button
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        style={{
          background: 'transparent',
          border: 'none',
          borderTop: '1px solid var(--color-border)',
          cursor: 'pointer',
          color: 'var(--color-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0.875rem',
          transition: 'color 0.15s',
        }}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>
    </aside>
  );
}


